// Localization for the static site text (everything not stored in the
// database). The language is detected from the browser (navigator.languages
// / navigator.language, most preferred first) and cannot be overridden, so
// the address bar stays a pure view/filter state.
//
// Strings live in the translations catalog below, keyed by BCP-47 language
// tag; adding a language means adding one entry, detection then picks it up
// automatically. Detection matches full tags first ("nl-NL"), then the
// primary subtag ("nl"), across every preferred language, and falls back to
// English for anything the catalog does not cover.
//
// Exposed API (used by products.js):
//   I18N.lang          detected language tag
//   I18N.t(key, params)      translated string; {param} placeholders are
//                      replaced from params, e.g. t('error.load_products',
//                      { message: err.message })
//   I18N.tCount(key, n)      count-aware translation for keys stored as a
//                      CLDR plural-category map ({one: ..., other: ...}),
//                      resolved with Intl.PluralRules for the language
//   I18N.applyStatic() applies translations to the [data-i18n],
//                      [data-i18n-placeholder] and
//                      [data-i18n-aria-label] elements of the page
(function () {
  'use strict';

  const translations = {
    en: {
      'page.title': 'ProductList',
      'nav.products': 'Products',
      'nav.login': 'Login',
      'nav.privacy': 'Privacy policy',
      'privacy.title': 'Privacy Policy',
      'privacy.heading': 'Privacy Policy',
      'privacy.meta_description': 'Privacy policy: which personal data this website processes, how, and for how long.',
      'privacy.intro': 'Updated 22 October 2026. Short version: this site collects nothing about you.',
      'privacy.collected': 'There are no accounts, forms, analytics, or trackers. Only two things touch personal data: your IP address, used briefly to rate-limit login attempts and discarded after 15 minutes, and a single session cookie ("admin_session") set only when you log in as administrator; it expires within 24 hours. Product data sent to an AI service to enrich catalog entries contains no personal data.',
      'privacy.not_collected': 'No names, email addresses, or other personal data is collected, stored, or shared with third parties.',
      'privacy.rights': 'If you have privacy questions or believe your data is processed anyway, contact the operator of this website.',
      'privacy.back': 'Back to the products',
      'nav.administration': 'Administration',
      'nav.back_to_main': 'Back to the main page',
      'login.title': 'Login',
      'login.heading': 'Login',
      'login.password_label': 'Password',
      'login.password_aria': 'Password',
      'login.submit': 'Log in',
      'login.error': 'Login failed: wrong password.',
      'login.error_network': 'Login failed: {message}',
      'admin.title': 'Administration',
      'admin.heading': 'Administration',
      'admin.hello': 'Hello world!',
      'admin.logout': 'Log out',
      'admin.types.heading': 'Product types',
      'admin.types.hint': 'Every category of products and the properties its items can have. Select a type to see its property definitions.',
      'admin.types.add': 'Add type',
      'admin.types.add_aria': 'Add product type',
      'admin.types.remove_aria': 'Remove product type {name}',
      'admin.types.col_actions': 'Actions',
      'admin.properties.add': 'Add property',
      'admin.properties.remove_aria': 'Remove property {name}',
      'admin.status.removed': 'Removed.',
      'admin.confirm.remove_type': 'Remove product type "{name}" and all its properties?',
      'admin.confirm.remove_property': 'Remove property "{name}"?',
      'admin.dialog.cancel': 'Cancel',
      'admin.dialog.add': 'Add',
      'admin.dialog.remove': 'Remove',
      'admin.dialog.add_type_title': 'Add product type',
      'admin.dialog.add_property_title': 'Add property',
      'admin.dialog.remove_title': 'Remove product type',
      'admin.dialog.remove_property_title': 'Remove property',
      'admin.dialog.name_label': 'Name',
      'admin.types.col_name': 'Name',
      'admin.types.col_description': 'Description',
      'admin.types.col_properties': 'Properties',
      'admin.properties.hint': 'Property definitions of the selected product type.',
      'admin.properties.col_name': 'Name',
      'admin.properties.col_type': 'Type',
      'admin.properties.col_description': 'Description',
      'admin.properties.type_string': 'Text',
      'admin.properties.type_number': 'Number',
      'admin.properties.type_boolean': 'Yes/No',
      'admin.status.loading': 'Loading product types\u2026',
      'admin.status.no_types': 'No product types yet.',
      'admin.error.load_types': 'Failed to load product types: {message}',
      'admin.llm.heading': 'Enrichment model',
      'admin.llm.hint': 'The OpenRouter model the enrichment agent uses. The list is fetched live from OpenRouter; changing it applies to the next enrichment run without a restart.',
      'admin.llm.loading': 'Loading models\u2026',
      'admin.llm.default': "Agent's default model",
      'admin.llm.save': 'Save',
      'admin.llm.load_failed': 'Model list unavailable',
      'admin.status.llm_saved': 'Model saved.',
      'admin.error.llm_load': 'Failed to load models: {message}',
      'admin.error.llm_save': 'Failed to save model: {message}',
      'admin.edit.hint': 'Click the pencil to edit',
      'admin.edit.edit_aria': 'Edit cell',
      'admin.edit.save_aria': 'Save cell',
      'admin.edit.delete_aria': 'Remove this value from the product',
      'admin.error.save': 'Failed to save: {message}',
      'admin.error.create': 'Failed to add: {message}',
      'admin.error.remove': 'Failed to remove: {message}',
      'filter.all_categories': 'All categories',
      'filter.by_category_aria': 'Filter by category',
      'filter.add': '+ Filter',
      'search.placeholder': 'Search products\u2026',
      'search.aria': 'Search products',
      'filter.property_aria': 'Property to filter by',
      'filter.operator_aria': 'Filter operator',
      'op.equals': 'equals',
      'op.is_set': 'is set',
      'op.is_unset': 'is unset',
      'filter.apply': 'Apply',
      'filter.cancel': 'Cancel',
      'filter.update': 'Update',
      'col.brand': 'Brand',
      'col.model': 'Model',
      'col.type': 'Type',
      'col.listings': 'Listings',
      'status.loading': 'Loading products\u2026',
      'status.scroll_more': 'Scroll to load more products\u2026',
      'status.all_loaded': 'All products loaded',
      'status.no_matches': 'No matching products',
      'count.matches': { one: 'match', other: 'matches' },
      'count.products': { one: 'product', other: 'products' },
      'count.products_in_category': { one: 'product in category', other: 'products in category' },
      'error.load_products': 'Failed to load products: {message}',
      'error.load_categories': 'Failed to load categories: {message}',
      'error.load_properties': 'Failed to load properties: {message}',
      'error.unknown_type': 'Unknown product type; reload the page and try again',
      'error.invalid_number': 'Value must be a number',
      'chip.is_set': '{name} is set',
      'chip.is_unset': '{name} is unset',
      'value.yes': 'Yes',
      'value.no': 'No',
      'chip.edit_aria': 'Edit filter {name}',
      'chip.remove_aria': 'Remove filter {name}',
      'row.delete_aria': 'Delete product {brand} {model}',
      'row.complete_aria': 'Find missing properties for {brand} {model} with AI',
      'confirm.delete_product': 'Delete product "{brand} {model}"?',
      'confirm.remove_property_value': 'Remove this property value from the product?',
      'status.agent_started': 'Agent is looking up the missing properties\u2026',
      'status.agent_done': 'Agent finished updating the product.',
      'status.agent_failed': 'Agent run failed: {message}',
      'prop.pending_aria': 'Looking up this property with AI\u2026',
      'prop.tooltip_no_value': 'AI lookup found no value; the AI button retries this',
      'prop.tooltip_not_applicable': 'This property does not apply to this product',
      'row.logs_aria': 'View AI enrichment logs for {brand} {model}',
      'logs.title': 'AI enrichment logs \u2014 {brand} {model}',
      'logs.close': 'Close',
      'logs.empty': 'No enrichment runs logged for this product yet.',
      'logs.load_failed': 'Failed to load enrichment logs: {message}',
      'logs.default_model': 'default model',
      'logs.run_failed': 'Run failed',
    },
    nl: {
      'page.title': 'ProductList',
      'nav.products': 'Producten',
      'nav.login': 'Inloggen',
      'nav.privacy': 'Privacybeleid',
      'privacy.title': 'Privacybeleid',
      'privacy.heading': 'Privacybeleid',
      'privacy.meta_description': 'Privacybeleid: welke persoonsgegevens deze website verwerkt, hoe en hoe lang.',
      'privacy.intro': 'Bijgewerkt 22 oktober 2026. Kort samengevat: deze site verzamelt niets over u.',
      'privacy.collected': 'Er zijn geen accounts, formulieren, analytics of trackers. Slechts twee dingen raken persoonsgegevens: uw IP-adres, dat korte tijd wordt gebruikt om inlogpogingen te beperken en na 15 minuten wordt verwijderd, en \u00e9\u00e9n sessiecookie ("admin_session") dat alleen wordt gezet als u als beheerder inlogt; het verloopt binnen 24 uur. Productgegevens die naar een AI-dienst worden gestuurd om catalogusitems aan te vullen bevatten geen persoonsgegevens.',
      'privacy.not_collected': 'Er worden geen namen, e-mailadressen of andere persoonsgegevens verzameld, opgeslagen of gedeeld met derden.',
      'privacy.rights': 'Heeft u privacyvragen of vindt u toch dat uw gegevens worden verwerkt, neem dan contact op met de beheerder van deze website.',
      'privacy.back': 'Terug naar de producten',
      'nav.administration': 'Beheer',
      'nav.back_to_main': 'Terug naar de hoofdpagina',
      'login.title': 'Inloggen',
      'login.heading': 'Inloggen',
      'login.password_label': 'Wachtwoord',
      'login.password_aria': 'Wachtwoord',
      'login.submit': 'Inloggen',
      'login.error': 'Inloggen mislukt: verkeerd wachtwoord.',
      'login.error_network': 'Inloggen mislukt: {message}',
      'admin.title': 'Beheer',
      'admin.heading': 'Beheer',
      'admin.hello': 'Hallo wereld!',
      'admin.logout': 'Uitloggen',
      'admin.types.heading': 'Producttypen',
      'admin.types.hint': 'Alle productcategorie\u00ebn en de eigenschappen die hun artikelen kunnen hebben. Selecteer een type om de eigenschapsdefinities te zien.',
      'admin.types.add': 'Type toevoegen',
      'admin.types.add_aria': 'Producttype toevoegen',
      'admin.types.remove_aria': 'Producttype {name} verwijderen',
      'admin.types.col_actions': 'Acties',
      'admin.properties.add': 'Eigenschap toevoegen',
      'admin.properties.remove_aria': 'Eigenschap {name} verwijderen',
      'admin.status.removed': 'Verwijderd.',
      'admin.confirm.remove_type': 'Producttype "{name}" en alle eigenschappen verwijderen?',
      'admin.confirm.remove_property': 'Eigenschap "{name}" verwijderen?',
      'admin.dialog.cancel': 'Annuleren',
      'admin.dialog.add': 'Toevoegen',
      'admin.dialog.remove': 'Verwijderen',
      'admin.dialog.add_type_title': 'Producttype toevoegen',
      'admin.dialog.add_property_title': 'Eigenschap toevoegen',
      'admin.dialog.remove_title': 'Producttype verwijderen',
      'admin.dialog.remove_property_title': 'Eigenschap verwijderen',
      'admin.dialog.name_label': 'Naam',
      'admin.types.col_name': 'Naam',
      'admin.types.col_description': 'Omschrijving',
      'admin.types.col_properties': 'Eigenschappen',
      'admin.properties.hint': 'Eigenschapsdefinities van het geselecteerde producttype.',
      'admin.properties.col_name': 'Naam',
      'admin.properties.col_type': 'Type',
      'admin.properties.col_description': 'Omschrijving',
      'admin.properties.type_string': 'Tekst',
      'admin.properties.type_number': 'Getal',
      'admin.properties.type_boolean': 'Ja/Nee',
      'admin.status.loading': 'Producttypen worden geladen\u2026',
      'admin.status.no_types': 'Nog geen producttypen.',
      'admin.error.load_types': 'Kan producttypen niet laden: {message}',
      'admin.llm.heading': 'Verrijkingsmodel',
      'admin.llm.hint': 'Het OpenRouter-model dat de verrijkingsagent gebruikt. De lijst komt live van OpenRouter; een wijziging geldt voor de volgende verrijkingsrun zonder herstart.',
      'admin.llm.loading': 'Modellen worden geladen\u2026',
      'admin.llm.default': "Standaardmodel van de agent",
      'admin.llm.save': 'Opslaan',
      'admin.llm.load_failed': 'Modellenlijst niet beschikbaar',
      'admin.status.llm_saved': 'Model opgeslagen.',
      'admin.error.llm_load': 'Kan modellen niet laden: {message}',
      'admin.error.llm_save': 'Opslaan van model mislukt: {message}',
      'admin.edit.hint': 'Klik op het potlood om te bewerken',
      'admin.edit.edit_aria': 'Cel bewerken',
      'admin.edit.save_aria': 'Cel opslaan',
      'admin.edit.delete_aria': 'Deze waarde van het product verwijderen',
      'admin.error.save': 'Opslaan mislukt: {message}',
      'admin.error.create': 'Toevoegen mislukt: {message}',
      'admin.error.remove': 'Verwijderen mislukt: {message}',
      'filter.all_categories': 'Alle categorie\u00ebn',
      'filter.by_category_aria': 'Filteren op categorie',
      'filter.add': '+ Filter',
      'search.placeholder': 'Producten zoeken\u2026',
      'search.aria': 'Producten zoeken',
      'filter.property_aria': 'Eigenschap om op te filteren',
      'filter.operator_aria': 'Filteroperator',
      'op.equals': 'is gelijk aan',
      'op.is_set': 'is ingesteld',
      'op.is_unset': 'is niet ingesteld',
      'filter.apply': 'Toepassen',
      'filter.cancel': 'Annuleren',
      'filter.update': 'Bijwerken',
      'col.brand': 'Merk',
      'col.model': 'Model',
      'col.type': 'Type',
      'col.listings': 'Winkels',
      'status.loading': 'Producten worden geladen\u2026',
      'status.scroll_more': 'Scroll om meer producten te laden\u2026',
      'status.all_loaded': 'Alle producten geladen',
      'status.no_matches': 'Geen overeenkomende producten',
      'count.matches': { one: 'overeenkomst', other: 'overeenkomsten' },
      'count.products': { one: 'product', other: 'producten' },
      'count.products_in_category': { one: 'product in de categorie', other: 'producten in de categorie' },
      'error.load_products': 'Kan producten niet laden: {message}',
      'error.load_categories': 'Kan categorie\u00ebn niet laden: {message}',
      'error.load_properties': 'Kan eigenschappen niet laden: {message}',
      'error.unknown_type': 'Onbekend producttype; herlaad de pagina en probeer opnieuw',
      'error.invalid_number': 'Waarde moet een getal zijn',
      'chip.is_set': '{name} is ingesteld',
      'chip.is_unset': '{name} is niet ingesteld',
      'value.yes': 'Ja',
      'value.no': 'Nee',
      'chip.edit_aria': 'Filter {name} bewerken',
      'chip.remove_aria': 'Filter {name} verwijderen',
      'row.delete_aria': 'Product {brand} {model} verwijderen',
      'row.complete_aria': 'Ontbrekende eigenschappen van {brand} {model} met AI opzoeken',
      'confirm.delete_product': 'Product "{brand} {model}" verwijderen?',
      'confirm.remove_property_value': 'Deze eigenschapswaarde van het product verwijderen?',
      'status.agent_started': 'De agent zoekt de ontbrekende eigenschappen op\u2026',
      'status.agent_done': 'De agent heeft het product bijgewerkt.',
      'status.agent_failed': 'Agent-run mislukt: {message}',
      'prop.pending_aria': 'Deze eigenschap wordt met AI opgezocht\u2026',
      'prop.tooltip_no_value': 'AI vond geen waarde; de AI-knop probeert het opnieuw',
      'prop.tooltip_not_applicable': 'Deze eigenschap is niet van toepassing op dit product',
      'row.logs_aria': 'AI-verrijkingslogboeken van {brand} {model} bekijken',
      'logs.title': 'AI-verrijkingslogboeken \u2014 {brand} {model}',
      'logs.close': 'Sluiten',
      'logs.empty': 'Nog geen verrijkingsruns gelogd voor dit product.',
      'logs.load_failed': 'Kan verrijkingslogboeken niet laden: {message}',
      'logs.default_model': 'standaardmodel',
      'logs.run_failed': 'Run mislukt',
    },
  };

  // English is the fallback: it must define every key exactly once.
  const FALLBACK = 'en';

  // Legacy/alternate primary subtags that map onto catalog languages.
  const ALIASES = { no: 'nb', nn: 'nb', iw: 'he', in: 'id', tl: 'fil' };

  // Languages written right-to-left; <html dir> is set for these.
  const RTL = new Set(['ar', 'he', 'fa', 'ur']);

  // Resolve one language tag to a catalog key, or null when untranslated.
  // "nl-NL" tries the full tag, then the primary subtag; the alias map
  // covers subtags that no browser sends for these languages but legacy
  // clients might (e.g. Java's "iw" for Hebrew).
  function resolve(tag) {
    const parts = String(tag).toLowerCase().split('-').filter(Boolean);
    if (parts.length === 0) return null;
    const full = parts.join('-');
    if (full in translations) return full;
    const primary = ALIASES[parts[0]] || parts[0];
    return primary in translations ? primary : null;
  }

  // Walk the browser's preference list most-preferred first; English wins
  // only when nothing earlier matches.
  function detect() {
    const preferred = [];
    if (Array.isArray(navigator.languages)) preferred.push(...navigator.languages);
    if (navigator.language) preferred.push(navigator.language);
    for (const tag of preferred) {
      const lang = resolve(tag);
      if (lang) return lang;
    }
    return FALLBACK;
  }

  const lang = detect();

  function entry(key) {
    const dict = translations[lang];
    const value = dict ? dict[key] : undefined;
    if (value !== undefined) return value;
    return translations[FALLBACK][key];
  }

  // Replace {param} placeholders; unknown params are left verbatim so a
  // typo is visible instead of silently swallowed.
  function format(template, params) {
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
    );
  }

  function t(key, params) {
    const value = entry(key);
    return format(typeof value === 'string' ? value : key, params);
  }

  // Count-aware lookup: a string entry is returned as-is (for languages
  // without numeral agreement); a map entry is indexed by the CLDR plural
  // category of the count, falling back to the "other" form.
  function tCount(key, count) {
    const value = entry(key);
    if (typeof value !== 'object' || value === null) {
      return typeof value === 'string' ? value : key;
    }
    let category = 'other';
    try {
      category = new Intl.PluralRules(lang).select(Number(count));
    } catch (err) {
      /* no plural rules for this locale: use "other" */
    }
    return value[category] || value.other || key;
  }

  function applyStatic() {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang) ? 'rtl' : 'ltr';
    for (const el of document.querySelectorAll('[data-i18n]')) {
      el.textContent = t(el.getAttribute('data-i18n'));
    }
    for (const el of document.querySelectorAll('[data-i18n-placeholder]')) {
      el.setAttribute('placeholder', t(el.getAttribute('data-i18n-placeholder')));
    }
    for (const el of document.querySelectorAll('[data-i18n-aria-label]')) {
      el.setAttribute('aria-label', t(el.getAttribute('data-i18n-aria-label')));
    }
  }

  window.I18N = {
    lang,
    t,
    tCount,
    applyStatic,
    // The raw catalog, exposed so tests can verify every language covers
    // every key the English fallback defines.
    catalog: translations,
  };

  // The script sits at the end of <body>: during parsing the static text is
  // still being built, so wait for DOMContentLoaded; in tests (where the
  // script is evaluated after parsing) it runs immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyStatic);
  } else {
    applyStatic();
  }
})();
